import React, { useState, useEffect } from 'react';
import {
    X, IndianRupee, FileText, Edit, Building, MapPin, Briefcase,
    Clock, Calendar, Award, Monitor, ShieldAlert, Lock
} from 'lucide-react';
import { getFileUrl } from '../../utils/api';

/**
 * Full job posting details — laid out like the student placement detail page:
 * content sections on the left, a "Job Information" sidebar on the right.
 *
 * Extracted from JobManagement so the Job Fair detail page can show the exact
 * same view instead of carrying a second copy that would drift out of sync.
 *
 * @param job                {object|null} posting to display; renders nothing when falsy
 * @param onClose            {function}    close handler
 * @param onEdit             {function}    optional — omit to hide the Edit Job button
 * @param jobFairTitle       {string}      optional — name of the fair the posting is
 *                                         linked to, when the caller already knows it
 */

// One sidebar entry: icon tile + label over value. Mirrors the student page rows.
const InfoRow = ({ icon: Icon, label, value, accent = false }) => {
    if (!value) return null;
    return (
        <div className="flex gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#1a3884]/10 bg-white dark:border-[#4f7dff]/25 dark:bg-[#001430]">
                <Icon className={`h-[17px] w-[17px] ${accent ? 'text-rose-500' : 'text-[#1a3884] dark:text-[#4f7dff]'}`} strokeWidth={1.7} />
            </div>
            <div className="flex min-w-0 flex-col justify-center">
                <p className="text-[10.5px] font-medium uppercase tracking-[0.07em] text-slate-400">{label}</p>
                <p className={`mt-0.5 break-words text-[13.5px] font-semibold leading-tight ${accent ? 'text-rose-600 dark:text-rose-400' : 'text-[#0f225c] dark:text-white'}`}>
                    {value}
                </p>
            </div>
        </div>
    );
};

const SectionHeading = ({ children }) => (
    <h4 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#0f225c] dark:text-[#4f7dff]">
        <div className="h-2 w-2 rounded-full bg-[#1a3884] dark:bg-[#4f7dff]"></div>
        {children}
    </h4>
);

/**
 * 10th/12th benchmark as BOTH units — "75% / 7.5 CGPA". Jobs store either or
 * both; whichever is missing is derived by the ×10 convention used everywhere
 * else in the app.
 */
const formatSchoolScore = (has, pct, cgpa) => {
    if (!has) return 'Not Required';
    const p = pct ?? (cgpa != null ? Number((cgpa * 10).toFixed(1)) : null);
    const c = cgpa ?? (pct != null ? Number((pct / 10).toFixed(1)) : null);
    if (p == null && c == null) return 'Not Required';
    return `${p}% / ${c} CGPA`;
};

const JobDetailsModal = ({ job, onClose, onEdit, jobFairTitle = null }) => {
    // Long descriptions collapse behind a Read more toggle. Declared before the
    // null guard — hooks must run on every render — and reset per job so one
    // posting's expanded state never leaks into the next.
    const [descExpanded, setDescExpanded] = useState(false);
    useEffect(() => { setDescExpanded(false); }, [job?._id]);

    if (!job) return null;

    // jobFairId may arrive populated ({_id, title}) or as a bare id; a caller-supplied
    // title wins since it always has the fair in hand.
    const fairName = jobFairTitle || (typeof job.jobFairId === 'object' ? job.jobFairId?.title : null);
    const fmt = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm lg:left-64">
            <div className="bg-white dark:bg-[#001430] w-full max-w-4xl rounded-[24px] shadow-2xl overflow-hidden border border-[#1a3884]/20 animate-scale-in flex flex-col max-h-[90vh]">

                {/* Header — deep navy, one compact band */}
                <div className="bg-[#0d1f4e] px-6 py-4 shrink-0 dark:bg-[#0f225c]">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex min-w-0 items-center gap-3.5">
                            <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-white flex items-center justify-center text-sm font-semibold uppercase text-[#1a3884]">
                                {job.company?.logo ? (
                                    <img
                                        src={getFileUrl(job.company.logo)}
                                        alt={job.title}
                                        className="h-full w-full object-contain p-1"
                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                                    />
                                ) : null}
                                <span style={{ display: job.company?.logo ? 'none' : 'block' }}>
                                    {(job.company?.name || 'CO').slice(0, 2).toUpperCase()}
                                </span>
                            </div>
                            <div className="min-w-0">
                                <h3 className="truncate text-[17px] font-semibold leading-tight tracking-[-0.01em] text-white" title={job.title}>
                                    {job.title}
                                </h3>
                                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                                    <span className="truncate text-[12px] font-medium uppercase tracking-[0.06em] text-blue-200/90">
                                        {job.company?.name || 'Corporate Partner'}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 rounded bg-white/10 px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.05em] text-white ring-1 ring-white/25">
                                        <span className={`h-1.5 w-1.5 rounded-full ${
                                            job.status === 'open' ? 'bg-emerald-400' :
                                            job.status === 'upcoming' ? 'bg-amber-400' : 'bg-rose-400'
                                        }`} />
                                        {job.status?.toUpperCase() || 'N/A'}
                                    </span>
                                    {job.jobType && <span className="rounded bg-white/10 px-2 py-[2px] text-[10px] font-medium text-blue-100 ring-1 ring-white/20">{job.jobType}</span>}
                                    {job.workMode && <span className="rounded bg-white/10 px-2 py-[2px] text-[10px] font-medium text-blue-100 ring-1 ring-white/20">{job.workMode}</span>}
                                    {fairName && <span className="rounded bg-white/10 px-2 py-[2px] text-[10px] font-medium text-blue-100 ring-1 ring-white/20">{fairName}</span>}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="shrink-0 rounded-lg p-2 text-white/70 transition-colors hover:bg-white/15 hover:text-white"
                        >
                            <X className="h-[18px] w-[18px]" />
                        </button>
                    </div>
                </div>

                {/* Body — content left, Job Information sidebar right (student-page layout) */}
                <div className="overflow-y-auto flex-1 bg-[#f8fafc] dark:bg-[#001a3d] p-6">
                    <div className="grid gap-6 lg:grid-cols-[1.55fr_1fr] lg:items-start">

                        <div className="space-y-6 min-w-0">
                            {/* Moderation notice — flagged / locked postings show the exact
                                issues and how many fix attempts remain. */}
                            {job.moderationStatus === 'flagged' && (
                                <div className={`rounded-2xl border p-5 shadow-sm ${job.moderationLocked
                                    ? 'bg-slate-800 border-slate-700 text-white'
                                    : 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900/40 text-rose-900 dark:text-rose-200'}`}>
                                    <div className="flex items-start gap-3">
                                        {job.moderationLocked
                                            ? <Lock className="w-5 h-5 shrink-0 mt-0.5" />
                                            : <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5 text-rose-600 dark:text-rose-400" />}
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-bold">
                                                {job.moderationLocked
                                                    ? 'Locked after 3 failed moderation attempts'
                                                    : `Flagged by moderation — attempt ${job.moderationAttempts || 1} of 3`}
                                            </p>
                                            <p className={`text-xs mt-1 ${job.moderationLocked ? 'text-slate-300' : 'text-rose-700 dark:text-rose-300'}`}>
                                                {job.moderationLocked
                                                    ? 'Hidden from students and no longer editable. Delete it and post a fresh listing.'
                                                    : `Hidden from students until fixed. Edit and save again — ${Math.max(0, 3 - (job.moderationAttempts || 1))} attempt${Math.max(0, 3 - (job.moderationAttempts || 1)) === 1 ? '' : 's'} left before it locks.`}
                                            </p>
                                            {Array.isArray(job.moderationFlags) && job.moderationFlags.length > 0 && (
                                                <ul className="mt-3 space-y-1.5">
                                                    {job.moderationFlags.map((f, i) => (
                                                        <li key={i} className={`text-xs flex items-start gap-2 ${job.moderationLocked ? 'text-slate-200' : 'text-rose-800 dark:text-rose-200'}`}>
                                                            <span className={`shrink-0 mt-[3px] w-1.5 h-1.5 rounded-full ${f.severity === 'Hard Block' ? 'bg-rose-500' : 'bg-amber-500'}`}></span>
                                                            <span><span className="font-semibold">{f.category}</span>{f.matchedPhrase ? <> — “{f.matchedPhrase}”</> : null}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Description */}
                            <div className="space-y-3">
                                <SectionHeading>Job Description</SectionHeading>
                                <div className="bg-white dark:bg-[#002147] border border-[#1a3884]/10 dark:border-[#4f7dff]/20 shadow-sm p-5 rounded-2xl">
                                    <div className={`text-[13.5px] text-[#334155] dark:text-slate-300 leading-[1.7] whitespace-pre-line ${!descExpanded ? 'line-clamp-5' : ''}`}>
                                        {job.description || 'No description provided.'}
                                    </div>
                                    {/* ~360 chars ≈ where five lines overflow at this width */}
                                    {(job.description?.length || 0) > 360 && (
                                        <button
                                            type="button"
                                            onClick={() => setDescExpanded(v => !v)}
                                            className="mt-2.5 text-[12.5px] font-bold text-[#1a3884] dark:text-[#4f7dff] hover:underline"
                                        >
                                            {descExpanded ? 'Show less' : 'Read more'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Benefits — only for postings that carry them */}
                            {job.benefits?.trim() && (
                                <div className="space-y-3">
                                    <SectionHeading>Benefits</SectionHeading>
                                    <div className="bg-white dark:bg-[#002147] border border-[#1a3884]/10 dark:border-[#4f7dff]/20 shadow-sm p-5 rounded-2xl">
                                        <div className="text-[13.5px] text-[#334155] dark:text-slate-300 leading-[1.7] whitespace-pre-line">
                                            {job.benefits}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Eligibility Criteria */}
                            <div className="space-y-3">
                                <SectionHeading>Eligibility Criteria</SectionHeading>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Academic Requirements */}
                                    <div className="p-5 bg-white dark:bg-[#002147] border border-[#1a3884]/10 dark:border-[#4f7dff]/20 shadow-sm rounded-2xl space-y-4">
                                        <p className="text-[10.5px] font-medium text-[#1a3884]/70 dark:text-[#4f7dff]/70 uppercase tracking-[0.07em] border-b border-[#1a3884]/5 dark:border-[#4f7dff]/10 pb-2">Academic Requirements</p>
                                        <div className="flex justify-between items-center text-[13px] border-b border-slate-50 dark:border-slate-800/50 pb-2.5">
                                            <span className="text-slate-600 dark:text-slate-400">Min. CGPA</span>
                                            <span className="font-semibold text-[#1a3884] dark:text-white bg-[#1a3884]/10 dark:bg-[#4f7dff]/20 px-2.5 py-0.5 rounded-md border border-[#1a3884]/20 dark:border-[#4f7dff]/30">{job.eligibility?.minCGPA || '0.0'}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-[13px] border-b border-slate-50 dark:border-slate-800/50 pb-2.5">
                                            <span className="text-slate-600 dark:text-slate-400">Backlogs</span>
                                            <span className={`font-semibold px-2.5 py-0.5 rounded-md text-[11px] tracking-wide ${job.eligibility?.noBacklog ? 'text-rose-700 bg-rose-100 border border-rose-200' : 'text-emerald-700 bg-emerald-100 border border-emerald-200'}`}>
                                                {job.eligibility?.noBacklog ? 'Not Allowed' : 'Allowed'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-[13px] border-b border-slate-50 dark:border-slate-800/50 pb-2.5">
                                            <span className="text-slate-600 dark:text-slate-400">Min 10th Criteria</span>
                                            <span className="font-semibold text-[#0f225c] dark:text-white text-[13px]">
                                                {formatSchoolScore(job.eligibility?.hasMin10th, job.eligibility?.min10thPercentage, job.eligibility?.min10thCGPA)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-[13px]">
                                            <span className="text-slate-600 dark:text-slate-400">Min 12th Criteria</span>
                                            <span className="font-semibold text-[#0f225c] dark:text-white text-[13px]">
                                                {formatSchoolScore(job.eligibility?.hasMin12th, job.eligibility?.min12thPercentage, job.eligibility?.min12thCGPA)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Degrees & Branches */}
                                    <div className="p-5 bg-white dark:bg-[#002147] border border-[#1a3884]/10 dark:border-[#4f7dff]/20 shadow-sm rounded-2xl space-y-4">
                                        <p className="text-[10.5px] font-medium text-[#1a3884]/70 dark:text-[#4f7dff]/70 uppercase tracking-[0.07em] border-b border-[#1a3884]/5 dark:border-[#4f7dff]/10 pb-2">Degrees &amp; Specializations</p>
                                        <div className="space-y-2">
                                            <p className="text-[12px] text-slate-500 dark:text-slate-400 font-medium">Allowed Degrees</p>
                                            <div className="flex flex-wrap gap-2">
                                                {job.eligibility?.allowedDegrees?.length > 0 ? (
                                                    job.eligibility.allowedDegrees.map((deg, idx) => (
                                                        <span key={idx} className="px-2.5 py-1 bg-[#1a3884]/5 dark:bg-[#1a3884]/40 text-[#1a3884] dark:text-[#4f7dff] rounded-md text-[11.5px] font-medium border border-[#1a3884]/15 dark:border-[#4f7dff]/30">{deg}</span>
                                                    ))
                                                ) : <span className="text-[12px] text-slate-400 italic">Open to All Degrees</span>}
                                            </div>
                                        </div>
                                        <div className="space-y-2 pt-1">
                                            <p className="text-[12px] text-slate-500 dark:text-slate-400 font-medium">Allowed Specializations</p>
                                            <div className="flex flex-wrap gap-2">
                                                {job.eligibility?.allowedBranches?.length > 0 ? (
                                                    job.eligibility.allowedBranches.map((br, idx) => (
                                                        <span key={idx} className="px-2.5 py-1 bg-[#0f225c] text-white rounded-md text-[11.5px] font-medium shadow-sm">{br}</span>
                                                    ))
                                                ) : <span className="text-[12px] text-slate-400 italic">Open to All Specializations</span>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Project Domains */}
                            {job.projectDomains?.length > 0 && (
                                <div className="space-y-3">
                                    <SectionHeading>Project Domains</SectionHeading>
                                    <div className="p-5 bg-white dark:bg-[#002147] border border-[#1a3884]/10 dark:border-[#4f7dff]/20 shadow-sm rounded-2xl">
                                        <div className="flex flex-wrap gap-2">
                                            {job.projectDomains.map((d, i) => (
                                                <span key={i} className="text-[11px] font-semibold text-[#1a3884] dark:text-[#4f7dff] bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 border border-[#1a3884]/15 dark:border-[#4f7dff]/25 px-2.5 py-1 rounded-md">{d}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* SMAART Course Skills */}
                            {job.courseSkills?.length > 0 && (
                                <div className="space-y-3">
                                    <SectionHeading>Human Intelligence Courses</SectionHeading>
                                    <div className="p-5 bg-white dark:bg-[#002147] border border-[#1a3884]/10 dark:border-[#4f7dff]/20 shadow-sm rounded-2xl">
                                        <div className="flex flex-wrap gap-2">
                                            {job.courseSkills.map((c, i) => (<span key={i} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#1a3884] dark:text-[#4f7dff] bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 border border-[#1a3884]/15 dark:border-[#4f7dff]/25 px-2.5 py-1 rounded-md">{c.title}</span>))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Required Skills */}
                            {job.structuredSkills?.length > 0 && (
                                <div className="space-y-3">
                                    <SectionHeading>Required Skills</SectionHeading>
                                    <div className="p-5 bg-white dark:bg-[#002147] border border-[#1a3884]/10 dark:border-[#4f7dff]/20 shadow-sm rounded-2xl">
                                        <div className="flex flex-wrap gap-2.5">
                                            {job.structuredSkills.map((skill, idx) => (
                                                <span key={idx} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#f1f5f9] dark:bg-[#001430] text-[#0f225c] dark:text-white border border-[#e2e8f0] dark:border-[#1a3884]/50 rounded-lg text-[12.5px] font-medium shadow-sm">
                                                    {skill.skill_name}
                                                    {skill.requiredLevel && (
                                                        <span className="px-1.5 py-0.5 bg-[#1a3884]/10 dark:bg-[#4f7dff]/20 text-[#1a3884] dark:text-[#4f7dff] rounded text-[10px] font-semibold">L{skill.requiredLevel}</span>
                                                    )}
                                                    {skill.isEssential && (
                                                        <span className="px-1.5 py-0.5 bg-rose-100 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400 rounded text-[10px] font-semibold uppercase tracking-[0.06em]">Must</span>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* JD Document */}
                            {job.jdDocument && (
                                <div className="p-5 bg-gradient-to-r from-[#1a3884]/5 to-transparent border border-[#1a3884]/15 rounded-2xl flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-4 min-w-0">
                                        <div className="w-12 h-12 rounded-xl bg-[#1a3884]/10 flex items-center justify-center shrink-0">
                                            <FileText className="w-6 h-6 text-[#1a3884] dark:text-[#4f7dff]" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[13.5px] font-semibold text-[#0f225c] dark:text-white">Job Description Document</p>
                                            <p className="text-[12px] text-slate-500 mt-0.5 truncate">{job.jdDocumentName || 'JD_Attachment.pdf'}</p>
                                        </div>
                                    </div>
                                    <a
                                        href={job.jdDocument}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="px-5 py-2.5 bg-white dark:bg-[#001430] border-2 border-[#1a3884]/20 hover:border-[#1a3884] text-[#1a3884] dark:text-[#4f7dff] font-medium text-[13px] rounded-xl shadow-sm transition-all hover:shadow-md active:scale-95 shrink-0"
                                    >
                                        View PDF
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Job Information sidebar — same shape as the student page */}
                        <aside className="rounded-2xl border border-[#1a3884]/10 bg-white p-5 shadow-sm dark:border-[#4f7dff]/20 dark:bg-[#002147] lg:sticky lg:top-0">
                            <h4 className="mb-4 border-b border-[#1a3884]/10 pb-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#1a3884] dark:border-[#4f7dff]/20 dark:text-[#4f7dff]">
                                Job Information
                            </h4>
                            <div className="grid grid-cols-1 gap-4">
                                {job.jobId && <InfoRow icon={FileText} label="Job ID" value={job.jobId} />}
                                <InfoRow icon={Building} label="Company" value={job.company?.name || job.companyName} />
                                {job.postedByBranchId && <InfoRow icon={Building} label="Posted By (Branch)" value={job.postedByBranchId} />}
                                {job.postedByRecruiterCode && <InfoRow icon={Building} label="Posted By (Recruiter)" value={job.postedByRecruiterCode} />}
                                {(job.branch || job.branchName) && (
                                    <InfoRow icon={MapPin} label="Branch" value={job.branch || job.branchName} />
                                )}
                                <InfoRow
                                    icon={MapPin}
                                    label="Location"
                                    value={
                                        job.location ? (
                                            job.location.includes(',') ? (
                                                <span className="flex flex-wrap gap-1 mt-1">
                                                    {job.location.split(',').map(s => s.trim()).filter(Boolean).map((loc, i) => (
                                                        <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-50 text-[#1a3884] dark:bg-[#4f7dff]/20 dark:text-[#9bb8ff] border border-blue-200/60 dark:border-[#4f7dff]/30">
                                                            {loc}
                                                        </span>
                                                    ))}
                                                </span>
                                            ) : job.location
                                        ) : 'Remote'
                                    }
                                />
                                <InfoRow icon={IndianRupee} label="Package" value={job.salaryPackage ? (job.salaryPackage.toString().startsWith('₹') ? job.salaryPackage : (job.salaryPackage.toString().toLowerCase() === 'unpaid' ? 'Unpaid' : `₹ ${job.salaryPackage}`)) : 'Not disclosed'} />
                                <InfoRow icon={Award} label="Experience" value={job.experience || 'Freshers'} />
                                <InfoRow icon={Briefcase} label="Job Type" value={job.jobType} />
                                <InfoRow icon={Monitor} label="Work Mode" value={job.workMode} />
                                {fairName && <InfoRow icon={Briefcase} label="Job Fair" value={fairName} />}
                                <InfoRow icon={Clock} label="Posted" value={job.createdAt ? fmt(job.createdAt) : null} />
                                <InfoRow icon={Calendar} label="Deadline" value={job.deadline ? fmt(job.deadline) : 'No closing date set'} accent={!!job.deadline} />
                                {job.status === 'upcoming' && job.openingDate && (
                                    <InfoRow icon={Calendar} label="Drive Opens On" value={fmt(job.openingDate)} accent />
                                )}
                            </div>
                        </aside>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-[#1a3884]/10 bg-white dark:bg-[#001430] shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2.5 text-[13.5px] font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700 dark:hover:bg-[#1a3884]/20 dark:hover:text-white border border-transparent hover:border-slate-200 dark:hover:border-[#1a3884]/30 rounded-xl transition-all"
                    >
                        Close
                    </button>
                    {onEdit && (
                        <button
                            type="button"
                            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#1a3884] hover:bg-[#0f225c] text-white text-[13.5px] font-medium rounded-xl shadow-lg shadow-[#1a3884]/20 transition-all active:scale-95"
                            onClick={() => { onClose(); onEdit(job); }}
                        >
                            <Edit className="w-4 h-4" /> Edit Job
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default JobDetailsModal;
